import { useState, useEffect } from 'react'
import {
  getInstallmentAlertSettings,
  saveInstallmentAlertSettings,
  ALERT_TIMING_OPTIONS,
} from '../api/installmentsApi'
import { appToast } from '../../../../components/AppToast'

const RECIPIENTS = [
  { label: 'Student', smsKey: 'studentSms', emailKey: 'studentEmail', waKey: 'studentWhatsApp', icon: 'school' },
  { label: 'Father', smsKey: 'fatherSms', emailKey: 'fatherEmail', waKey: 'fatherWhatsApp', icon: 'person' },
  { label: 'Mother', smsKey: 'motherSms', emailKey: 'motherEmail', waKey: 'motherWhatsApp', icon: 'person_3' },
  { label: 'Company / Institute', smsKey: 'companySms', emailKey: 'companyEmail', waKey: 'companyWhatsApp', icon: 'business' },
  { label: 'Branch', smsKey: 'branchSms', emailKey: 'branchEmail', waKey: 'branchWhatsApp', icon: 'apartment' },
  { label: 'Agent', smsKey: 'agentSms', emailKey: 'agentEmail', waKey: 'agentWhatsApp', icon: 'support_agent' },
]

export default function InstallmentsSettingsModal({ isOpen, onClose }) {
  const [settings, setSettings] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadSettings()
    }
  }, [isOpen])

  const loadSettings = async () => {
    setIsLoading(true)
    try {
      const data = await getInstallmentAlertSettings()
      setSettings(data || {})
    } catch (err) {
      console.error('Failed to load alert settings:', err)
      appToast.error('Failed to load installment alert settings.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleTimingChange = (key, value) => {
    setSettings((prev) => ({
      ...prev,
      [key]: Number(value),
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await saveInstallmentAlertSettings(settings)
      appToast.success('Installment alert settings saved successfully.')
      onClose()
    } catch (err) {
      console.error('Failed to save alert settings:', err)
      appToast.error('Error saving installment alert settings.')
    } finally {
      setIsSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs transition-opacity"
    >
      <div className="relative flex w-full max-w-4xl flex-col rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-2xl overflow-hidden animate-in fade-in-50 zoom-in-95 duration-200 max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/15 bg-surface-container-low/40 px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-2xl">notifications_active</span>
            </div>
            <div>
              <h2 className="text-lg font-extrabold tracking-tight text-on-surface">
                Installment Alerts Configuration
              </h2>
              <p className="text-xs text-on-surface-variant font-medium">
                Set automated reminders prior to or on installment due dates
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-xl text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer"
            aria-label="Close modal"
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 text-on-surface-variant gap-3">
              <span className="material-symbols-outlined animate-spin text-3xl text-primary/60">
                progress_activity
              </span>
              <p className="text-xs font-semibold">Loading alert preferences...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-low/20 overflow-hidden">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-outline-variant/20 bg-surface-container-low/70 text-[11px] font-black uppercase tracking-wider text-on-surface-variant select-none">
                      <th className="px-5 py-3.5">Recipient</th>
                      <th className="px-4 py-3.5 text-center">SMS Alert</th>
                      <th className="px-4 py-3.5 text-center">Email Alert</th>
                      <th className="px-4 py-3.5 text-center">WhatsApp Alert</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/15">
                    {RECIPIENTS.map((rec) => (
                      <tr key={rec.label} className="hover:bg-surface-container-low/40 transition-colors">
                        <td className="px-5 py-3 font-bold text-on-surface">
                          <div className="flex items-center gap-2.5">
                            <span className="material-symbols-outlined text-base text-primary/70">
                              {rec.icon}
                            </span>
                            <span>{rec.label}</span>
                          </div>
                        </td>

                        {/* SMS Dropdown */}
                        <td className="px-4 py-3 text-center">
                          <select
                            value={settings[rec.smsKey] ?? -1}
                            onChange={(e) => handleTimingChange(rec.smsKey, e.target.value)}
                            className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-2.5 text-xs font-medium text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/30 cursor-pointer"
                          >
                            {ALERT_TIMING_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Email Dropdown */}
                        <td className="px-4 py-3 text-center">
                          <select
                            value={settings[rec.emailKey] ?? -1}
                            onChange={(e) => handleTimingChange(rec.emailKey, e.target.value)}
                            className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-2.5 text-xs font-medium text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/30 cursor-pointer"
                          >
                            {ALERT_TIMING_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* WhatsApp Dropdown */}
                        <td className="px-4 py-3 text-center">
                          <select
                            value={settings[rec.waKey] ?? -1}
                            onChange={(e) => handleTimingChange(rec.waKey, e.target.value)}
                            className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-2.5 text-xs font-medium text-on-surface outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/30 cursor-pointer"
                          >
                            {ALERT_TIMING_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="rounded-2xl border border-primary/20 bg-primary/5 p-4 text-xs text-on-surface-variant flex items-start gap-3">
                <span className="material-symbols-outlined text-primary text-lg shrink-0 mt-0.5">info</span>
                <p>
                  Alert schedules run daily through the ERP background dispatcher. Notifications are sent automatically to primary contact numbers and emails according to the selected schedule.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-outline-variant/15 bg-surface-container-low/30 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-5 py-2 text-xs font-bold text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || isLoading}
            className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <span className="material-symbols-outlined animate-spin text-sm">progress_activity</span>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-sm">save</span>
                <span>Save Alert Settings</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
